import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { useCurrency } from '../../../contexts/CurrencyContext';
import projectService from '../../../services/projectService';

const ProjectTimeline = ({ projects, onEdit, onDelete }) => {
  const { formatCurrency } = useCurrency();

  // Sort projects by planned start date
  const sortedProjects = [...projects].sort((a, b) => {
    const dateA = a?.planned_start_date ? new Date(a.planned_start_date) : new Date(0);
    const dateB = b?.planned_start_date ? new Date(b.planned_start_date) : new Date(0);
    return dateA - dateB;
  });

  const getStatusColor = (status) => {
    const colors = {
      planned: 'var(--color-muted-foreground)',
      in_progress: 'var(--color-primary)',
      on_hold: 'var(--color-warning)',
      completed: 'var(--color-success)',
      cancelled: 'var(--color-error)'
    };
    return colors?.[status] || 'var(--color-muted-foreground)';
  };

  const getStatusLabel = (status) => {
    const labels = {
      planned: 'Planifié',
      in_progress: 'En cours',
      on_hold: 'En attente',
      completed: 'Terminé',
      cancelled: 'Annulé'
    };
    return labels?.[status] || status;
  };

  if (sortedProjects.length === 0) {
    return (
      <div className="bg-card border border-border rounded-xl p-12 text-center">
        <Icon name="Calendar" size={48} className="mx-auto mb-4 text-muted-foreground" />
        <p className="text-muted-foreground">Aucun projet à afficher dans l'échéancier</p>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-xl p-5 md:p-7">
      <div className="relative">
        {/* Timeline line */}
        <div style={{
          position: 'absolute',
          left: '24px',
          top: 0,
          bottom: 0,
          width: '2px',
          background: 'var(--color-border)'
        }} />

        {/* Projects */}
        <div className="space-y-6">
          {sortedProjects.map((project, index) => {
            const startDate = project?.planned_start_date ? new Date(project.planned_start_date) : null;
            const endDate = project?.planned_end_date ? new Date(project.planned_end_date) : null;
            
            return (
              <div key={project?.id} style={{
                position: 'relative',
                paddingLeft: '56px',
                display: 'flex',
                alignItems: 'flex-start',
                gap: 'var(--spacing-md)'
              }}>
                {/* Timeline dot */}
                <div style={{
                  position: 'absolute',
                  left: '16px',
                  top: '8px',
                  width: '16px',
                  height: '16px',
                  borderRadius: '50%',
                  background: getStatusColor(project?.status),
                  border: '3px solid var(--color-card)',
                  zIndex: 10
                }} />

                {/* Project card */}
                <div style={{
                  flex: 1,
                  background: 'var(--color-card)',
                  border: '1px solid var(--color-border)',
                  borderRadius: 'var(--radius-lg)',
                  padding: 'var(--spacing-md)',
                  transition: 'all var(--transition-base)'
                }}
                className="hover-lift"
                >
                  <div style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    marginBottom: 'var(--spacing-sm)'
                  }}>
                    <div style={{ flex: 1 }}>
                      <h3 style={{
                        fontFamily: 'var(--font-heading)',
                        fontSize: '1.125rem',
                        fontWeight: 600,
                        color: 'var(--color-foreground)',
                        marginBottom: '4px'
                      }}>
                        {project?.name}
                      </h3>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'var(--spacing-xs)',
                        marginBottom: 'var(--spacing-xs)'
                      }}>
                        <span style={{
                          fontFamily: 'var(--font-caption)',
                          fontSize: '0.75rem',
                          color: getStatusColor(project?.status),
                          background: `${getStatusColor(project?.status)}15`,
                          padding: '2px 8px',
                          borderRadius: 'var(--radius-sm)'
                        }}>
                          {getStatusLabel(project?.status)}
                        </span>
                      </div>
                    </div>
                    <div style={{
                      display: 'flex',
                      gap: 'var(--spacing-xs)'
                    }}>
                      <Button
                        variant="ghost"
                        size="icon"
                        iconName="Edit"
                        onClick={() => onEdit(project)}
                      />
                      <Button
                        variant="ghost"
                        size="icon"
                        iconName="Trash2"
                        onClick={() => {
                          if (window.confirm('Êtes-vous sûr de vouloir supprimer ce projet ?')) {
                            onDelete(project?.id);
                          }
                        }}
                      />
                    </div>
                  </div>

                  {project?.description && (
                    <p style={{
                      fontFamily: 'var(--font-body)',
                      fontSize: '0.875rem',
                      color: 'var(--color-muted-foreground)',
                      marginBottom: 'var(--spacing-sm)',
                      lineHeight: 1.5
                    }}>
                      {project.description}
                    </p>
                  )}

                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
                    gap: 'var(--spacing-sm)',
                    marginBottom: 'var(--spacing-sm)'
                  }}>
                    <div>
                      <p style={{
                        fontFamily: 'var(--font-caption)',
                        fontSize: '0.75rem',
                        color: 'var(--color-muted-foreground)',
                        marginBottom: '2px'
                      }}>
                        Budget
                      </p>
                      <p style={{
                        fontFamily: 'var(--font-data)',
                        fontSize: '0.875rem',
                        fontWeight: 600,
                        color: 'var(--color-foreground)'
                      }}>
                        {formatCurrency(project?.estimated_cost)}
                      </p>
                    </div>
                    {startDate && (
                      <div>
                        <p style={{
                          fontFamily: 'var(--font-caption)',
                          fontSize: '0.75rem',
                          color: 'var(--color-muted-foreground)',
                          marginBottom: '2px'
                        }}>
                          Début
                        </p>
                        <p style={{
                          fontFamily: 'var(--font-body)',
                          fontSize: '0.875rem',
                          color: 'var(--color-foreground)'
                        }}>
                          {startDate.toLocaleDateString('fr-CA')}
                        </p>
                      </div>
                    )}
                    {endDate && (
                      <div>
                        <p style={{
                          fontFamily: 'var(--font-caption)',
                          fontSize: '0.75rem',
                          color: 'var(--color-muted-foreground)',
                          marginBottom: '2px'
                        }}>
                          Fin
                        </p>
                        <p style={{
                          fontFamily: 'var(--font-body)',
                          fontSize: '0.875rem',
                          color: 'var(--color-foreground)'
                        }}>
                          {endDate.toLocaleDateString('fr-CA')}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default ProjectTimeline;
